import React, { useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Box,
  Container,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "../../components/SnackBarProvider";
import axiosInstance from "../../components/AxiosInstance";
import useSharedLink from "../../scenes/SharedLink";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const sharedLink = useSharedLink();
  const [email, setEmail] = useState(""); // Email input state
  const [loading, setLoading] = useState(false); // Loading indicator
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Function to send password reset link
  const handleVerifyEmailClick = async () => {
    console.log("Verifying email:", email);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      openSnackbar("Email is required", "error");
      return;
    }
    if (!emailRegex.test(email)) {
      openSnackbar("Please enter a valid email address", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = { email };
      console.log("Sending request to /reset-password:", payload);

      const response = await axiosInstance.post(
        `${sharedLink}/reset-password`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.status === 200) {
        openSnackbar("Password reset link sent successfully!", "success");
      } else {
        openSnackbar("Failed to send password reset link. Try again.", "error");
      }
    } catch (error) {
      console.error("Error sending reset link:", error);
      openSnackbar(error, "error"); // ✅ Handle all errors
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            marginTop: "20px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            marginTop: 12,
            marginLeft: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#87CEFA",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            width: "auto", // ✅ Auto width like the login box
            minWidth: "350px", // ✅ Prevents shrinking too much
            maxWidth: "400px", // ✅ Ensures it doesn't get too wide
            height: "350px",
          }}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleVerifyEmailClick();
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ color: "#000" }}
          >
            Reset Password
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#000",
              fontSize: "0.8rem",
              fontWeight: "500",
              mb: -1.5, // ✅ Moves the label closer to the input
              letterSpacing: "0.05em",
              alignSelf: "flex-start", // ✅ Aligns text to the left
            }}
          >
            Email <span style={{ color: "red" }}>*</span>
          </Typography>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            sx={{ backgroundColor: "#fff", borderRadius: "4px" }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: "#007bff",
              "&:hover": { backgroundColor: "#0056b3" },
              width: "180px", // ✅ Adjust button width
              height: "35px", // ✅ Reduce button height
              fontSize: "13px", // ✅ Make text slightly smaller
              padding: "8px 12px", // ✅ Reduce padding for compactness
              borderRadius: "40px",
            }}
          >
            Reset Password
          </Button>

          {/* 🔹 "Already signed up? Login" Link */}
          <Typography
            sx={{
              mt: 3,
              fontSize: "1.1rem",
              fontWeight: "600", // ✅ Slightly bolder for emphasis
              textAlign: "center",
              color: "#1a1a1a", // ✅ Dark Gray (Better contrast)
            }}
          >
            <Link
              to="/"
              style={{
                textDecoration: "none",
                color: "#1565C0", // ✅ Deep Blue (Professional contrast)
                fontWeight: "bold",
                transition: "color 0.3s ease-in-out", // Smooth color transition effect
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px", // ✅ Spacing between icon & text
              }}
              onMouseEnter={(e) => (e.target.style.color = "#0d47a1")} // Darker blue on hover
              onMouseLeave={(e) => (e.target.style.color = "#1565C0")} // Restore original color
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "20px", // ✅ Ensures a proper circular shape
                  height: "20px",
                  borderRadius: "50%", // ✅ Makes it a perfect circle
                  backgroundColor: "#00C853", // ✅ Green background
                  color: "white", // ✅ White tick color for contrast
                  fontSize: "1rem",
                  fontWeight: "bold",
                  marginBottom: "2px",
                }}
              >
                ✔
              </span>
              Already have an account?
            </Link>
          </Typography>
        </Box>
      )}

      {/* Snackbar Notifications */}
      <Snackbar
        open={!!openSnackbar.message}
        autoHideDuration={4000}
        onClose={() => openSnackbar("", "")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {openSnackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ForgotPassword;
